/**
 * Groups a flat DocumentDto[] into cards, per the backend's grouping keys:
 *  - documentsBatchId set, batch.grouped !== false  -> one card per Documents Batch
 *  - documentsBatchId set, batch.grouped === false   -> each document is its own
 *                                                       row (still tagged with batchId
 *                                                       for context), not merged with siblings
 *  - documentsBatchId null, answerId set             -> one card per (answerType, answerId)
 *                                                       business answer
 *  - both null                                       -> standalone card (not yet linked to anything)
 *
 * `batchesById` (id -> DocumentsBatchDto & counts) is used to label batch
 * cards with their name/aggregate counts when available, and to read the
 * `grouped` display flag.
 */
export const groupDocuments = (documents = [], batchesById = {}) => {
  const groups = new Map();

  documents.forEach((doc) => {
    const batch = doc.documentsBatchId != null ? batchesById[doc.documentsBatchId] : null;
    const isGroupedBatch = doc.documentsBatchId != null && batch?.grouped !== false;

    let key;
    let type;
    if (isGroupedBatch) {
      key = `batch-${doc.documentsBatchId}`;
      type = "batch";
    } else if (doc.answerId != null) {
      key = `answer-${doc.answerType}-${doc.answerId}`;
      type = "answer";
    } else if (doc.documentsBatchId != null) {
      key = `ungrouped-${doc.id}`;
      type = "ungrouped";
    } else {
      key = `standalone-${doc.id}`;
      type = "standalone";
    }

    if (!groups.has(key)) {
      groups.set(key, {
        key,
        type,
        batchId: doc.documentsBatchId ?? null,
        answerType: doc.answerType ?? null,
        answerId: doc.answerId ?? null,
        documents: [],
      });
    }
    groups.get(key).documents.push(doc);
  });

  return [...groups.values()].map((group) => {
    const batch = group.type === "batch" && group.batchId != null ? batchesById[group.batchId] : null;
    const parsedCount = batch?.parsedCount ?? group.documents.filter((d) => d.docSenseId).length;
    const documentCount = batch?.documentCount ?? group.documents.length;
    const reviewedCount = batch?.reviewedCount ?? group.documents.filter((d) => d.answerId != null).length;
    // No batch-level aggregate for this from the backend — always computed off the
    // (possibly filtered) documents actually loaded into this group.
    const verifiedCount = group.documents.filter((d) => d.isVerified).length;
    return {
      ...group,
      label: batch?.name || group.documents[0]?.fileName || "Untitled",
      documentCount,
      parsedCount,
      reviewedCount,
      verifiedCount,
    };
  }).sort((a, b) => {
    const aDate = a.documents[0]?.createdAt || "";
    const bDate = b.documents[0]?.createdAt || "";
    return bDate.localeCompare(aDate);
  });
};
